// Pick the colleagues to share something you own with — a Notemaker note
// (recipients can edit it) or a Meeting (read-only). Lists every active user in the branch (the same /users/all list
// the planner's assignee picker uses, but unscoped), pre-ticks the current
// recipients and hands the FULL set to `onSave`, so unticking someone removes
// their access. The caller persists it; this only picks and reports.
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Loader2, Search, Users, X } from "lucide-react";
import { toast } from "sonner";
import { getAllUsers, type User as ApiUser } from "../api/user";
import PersonAvatar from "./people/PersonAvatar";
import "./share-people-dialog.css";

export interface SharePeopleDialogProps {
  /** What is being shared, lower case ("note", "meeting") — used in the title and toasts. */
  noun: string;
  /** The item's display title, quoted under the dialog title. */
  itemTitle: string;
  /** The owner is never listed: you cannot share with yourself. */
  ownerId: string;
  /** Who can read it now; these start ticked. */
  initialUserIds: string[];
  /** Save the full recipient set; resolve with how many people now have access. */
  onSave: (userIds: string[]) => Promise<number>;
  onClose: () => void;
  /** What recipients may do, shown under the title. Defaults to read-only;
   *  Notemaker passes its own because shared notes are editable. */
  accessNote?: string;
}

const userId = (u: ApiUser) => u.id || u.user_id;

export default function SharePeopleDialog({
  noun, itemTitle, ownerId, initialUserIds, onSave, onClose,
  accessNote = "people you pick can read it, not edit it.",
}: SharePeopleDialogProps) {
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initialUserIds));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAllUsers()
      .then((res) => {
        if (cancelled) return;
        setUsers((res.users || []).filter((u) => userId(u) && userId(u) !== ownerId));
        setError(null);
      })
      .catch(() => { if (!cancelled) setError("Couldn't load the employee list."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ownerId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? users.filter((u) =>
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q) ||
          (u.role || "").toLowerCase().includes(q))
      : users;
    // Ticked people float to the top so the current recipients are visible
    // without scrolling.
    return [...list].sort((a, b) => Number(picked.has(userId(b))) - Number(picked.has(userId(a))) || (a.name || "").localeCompare(b.name || ""));
  }, [users, query, picked]);

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      const n = await onSave(Array.from(picked));
      toast.success(n
        ? `Shared with ${n} ${n === 1 ? "person" : "people"}`
        : `${noun.charAt(0).toUpperCase()}${noun.slice(1)} is no longer shared`);
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `Couldn't share the ${noun}.`);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="spd-backdrop" onMouseDown={onClose} data-testid="share-dialog">
      <div
        className="spd-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spd-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="spd-head">
          <span className="spd-icon"><Users size={16} aria-hidden="true" /></span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 id="spd-title" className="spd-title">Share {noun}</h2>
            <p className="spd-sub">“{itemTitle}” · {accessNote}</p>
          </div>
          <button type="button" className="gt-icon-btn is-sm" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="spd-search">
          <Search size={14} aria-hidden="true" />
          <input
            autoFocus
            className="spd-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search employees by name, email or role…"
            aria-label="Search employees"
          />
          {picked.size > 0 && (
            <button type="button" className="spd-clear" onClick={() => setPicked(new Set())}>
              Clear ({picked.size})
            </button>
          )}
        </div>

        <div className="spd-list" role="listbox" aria-multiselectable="true" aria-label="Employees">
          {loading ? (
            <div className="spd-state">
              <Loader2 size={18} className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> Loading employees…
            </div>
          ) : error ? (
            <div className="spd-state">{error}</div>
          ) : visible.length === 0 ? (
            <div className="spd-state">{users.length ? "No one matches that search." : "No other employees to share with."}</div>
          ) : (
            visible.map((u) => {
              const id = userId(u);
              const on = picked.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`spd-row${on ? " is-on" : ""}`}
                  onClick={() => toggle(id)}
                >
                  <span className={`spd-check${on ? " is-on" : ""}`} aria-hidden="true">
                    {on && <Check size={12} strokeWidth={3} />}
                  </span>
                  <PersonAvatar userId={id} name={u.name || u.email} size={28} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span className="spd-name">{u.name || u.email}</span>
                    <span className="spd-meta">{[u.role, u.email].filter(Boolean).join(" · ")}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <footer className="spd-foot">
          <span className="spd-count">
            {picked.size ? `${picked.size} selected` : "Nobody selected"}
          </span>
          <button type="button" className="gt-btn is-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="gt-btn is-primary" onClick={() => void save()} disabled={saving || loading}>
            {saving ? <Loader2 size={14} className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Users size={14} aria-hidden="true" />}
            {picked.size ? "Share" : "Save"}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
